import * as React from 'react';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Link from '@mui/joy/Link';
import Typography from '@mui/joy/Typography';
import { useTrail, animated } from "@react-spring/web";
import useMediaQuery from "@mui/material/useMediaQuery";

type ProjectsProps = {
  onClick: () => void;
};

type Project = {
  name: string;
  tag: string;
  description: string;
  link?: string;
};

// Add new projects here, the grid reflows on its own.
const projects: Project[] = [
  {
    name: "CardJoy",
    tag: "Live",
    description:
      "Celebrate life's moments with a digital card. Sign one together as a group, " +
      "send an invitation that collects RSVPs, or drop a holiday note in someone's inbox.",
    link: "https://cardjoy.app",
  },
  {
    name: "Active Sanctions",
    tag: "Open Source",
    description:
      "A library that keeps sanctions lists up to date and turns a compliance check " +
      "into a single call.",
    link: "https://babystep.tech/active_sanction",
  },
];

export default function Projects(props: ProjectsProps) {
  const title = "Things I've Built";
  const buttonText = "Work With Me";
  const isMobile = useMediaQuery("(max-width:600px)"); // Detect small screens
  // create a trail animation for each character in the message
  const trail = useTrail(title.length, {
    from: { opacity: 0, transform: "translateY(-20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
    config: { mass: 1, tension: 210, friction: 20 },
  });

  return (
    <Box sx={{p: 5}}>
      <Typography
        level="h1"
        fontWeight="xl"
        fontSize={isMobile ? "4rem" : "clamp(6.875rem, 2.3636rem + 2.1818vw, 3rem)"}
      >
        {trail.map((props, index) => (
          <animated.span key={index} style={props}>
            {title[index]}
          </animated.span>
        ))}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "100%" : "300px"}, 1fr))`,
          gap: isMobile ? 3 : 4,
          maxWidth: "1200px",
          mt: isMobile ? 2 : 3,
        }}
      >
        {projects.map((project) => (
          <ProjectCard key={project.name} project={project} isMobile={isMobile} />
        ))}
      </Box>

      <Button
        size={isMobile ? "md" : "lg"}
        onClick={props.onClick}
        endDecorator={<ArrowForward fontSize="large" />}
        sx={{mt: isMobile ? 3 : 4}}
      >
        {buttonText}
      </Button>
    </Box>
  );
}

// A single project. The name becomes an overlay link when the project has
// somewhere to go, which makes the whole card clickable and still keyboard reachable.
const ProjectCard: React.FC<{ project: Project; isMobile: boolean }> = ({
  project,
  isMobile,
}) => (
  <Box
    sx={{
      position: "relative",
      borderTop: "3px solid",
      borderColor: "primary.500",
      pt: 1.5,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap" }}>
      <Typography
        level="h3"
        fontSize={isMobile ? "1.75rem" : "2.25rem"}
        sx={{ transition: "color 0.2s ease" }}
      >
        {project.link ? (
          <Link
            overlay
            href={project.link}
            target="_blank"
            rel="noopener"
            underline="none"
            sx={{ color: "inherit", "&:hover": { color: "primary.500" } }}
          >
            {project.name}
          </Link>
        ) : (
          project.name
        )}
      </Typography>
      <Typography
        fontFamily="body"
        fontSize="0.7rem"
        textColor="primary.500"
        sx={{ letterSpacing: "0.12em", textTransform: "uppercase" }}
      >
        {project.tag}
      </Typography>
    </Box>
    <Typography
      fontFamily="body"
      fontSize={isMobile ? "0.9rem" : "1rem"}
      textColor="text.secondary"
      sx={{ mt: 1, lineHeight: 1.55 }}
    >
      {project.description}
    </Typography>
  </Box>
);
